/**
 * The station + charger selection rules behind the access ScopePicker, kept
 * pure so they can be tested without rendering it.
 *
 * A granted charger always brings its station (Warren, 2026-09-28):
 * "'All' chargers adds the location. Ticking any charger adds its location.
 * A grant whose chargers have no location sees nothing, and the picker
 * should make that impossible rather than the resolver silent about it."
 * The scope resolver ANDs location_ids and charger_ids, so a charger whose
 * station is not granted resolves to zero rows. Every transition below keeps
 * "each selected charger's station is selected" true; unticking a charger
 * leaves its station (a station alone is a valid grant).
 */

export interface ScopeSelection {
  locations: ReadonlySet<string>;
  chargers: ReadonlySet<string>;
}

export interface ScopeSelectionCharger {
  _id: string;
  location_id: string;
}

/** Tick or untick one charger. Ticking it ticks its station. */
export function toggleChargerSelection(
  sel: ScopeSelection,
  chargerId: string,
  locationId: string,
): { locations: Set<string>; chargers: Set<string> } {
  const chargers = new Set(sel.chargers);
  const locations = new Set(sel.locations);
  if (chargers.has(chargerId)) {
    chargers.delete(chargerId);
  } else {
    chargers.add(chargerId);
    locations.add(locationId);
  }
  return { locations, chargers };
}

/** Tick or untick a station. Unticking it clears its chargers. */
export function toggleLocationSelection(
  sel: ScopeSelection,
  locationId: string,
  stationChargerIds: readonly string[],
): { locations: Set<string>; chargers: Set<string> } {
  const locations = new Set(sel.locations);
  const chargers = new Set(sel.chargers);
  if (locations.has(locationId)) {
    locations.delete(locationId);
    for (const id of stationChargerIds) chargers.delete(id);
  } else {
    locations.add(locationId);
  }
  return { locations, chargers };
}

/** "All N": every charger at the station, and the station. */
export function selectAllChargersAt(
  sel: ScopeSelection,
  locationId: string,
  stationChargerIds: readonly string[],
): { locations: Set<string>; chargers: Set<string> } {
  const chargers = new Set(sel.chargers);
  for (const id of stationChargerIds) chargers.add(id);
  const locations = new Set(sel.locations);
  locations.add(locationId);
  return { locations, chargers };
}

/** "Clear": the station's chargers go; the station stays ticked. */
export function clearChargersAt(
  sel: ScopeSelection,
  stationChargerIds: readonly string[],
): { locations: Set<string>; chargers: Set<string> } {
  const chargers = new Set(sel.chargers);
  for (const id of stationChargerIds) chargers.delete(id);
  return { locations: new Set(sel.locations), chargers };
}

/**
 * The stations a charger selection needs: `selectedLocations` plus the
 * station of every selected charger. Repairs a draft loaded from an older
 * grant (the picker can no longer build the mismatch, but a stored grant
 * may hold one). A charger missing from `availableChargers` is left alone,
 * and with `availableLocationIds` only a station the picker lists is added
 * (a charger at a deactivated station must not add a station nobody sees).
 */
export function withChargerLocations(
  selectedLocations: ReadonlySet<string>,
  selectedChargers: ReadonlySet<string>,
  availableChargers: readonly ScopeSelectionCharger[],
  availableLocationIds?: ReadonlySet<string>,
): Set<string> {
  const locationOf = new Map(availableChargers.map((c) => [c._id, c.location_id]));
  const next = new Set(selectedLocations);
  for (const id of selectedChargers) {
    const loc = locationOf.get(id);
    if (loc && (!availableLocationIds || availableLocationIds.has(loc))) next.add(loc);
  }
  return next;
}
